import {
    SPARK,
    domainFor,
    formatShortDate,
    linePaths,
    scaleX,
    scaleY,
    summarise,
    type TrendMetric,
    type TrendPoint,
} from '@/lib/progress-trend';
import { DeltaChip, changeLabel } from './DeltaChip';

/**
 * One metric inside a group: large current value, the change, a sparkline
 * (#241 part B).
 *
 * This replaces the full-width chart P3 gave each of three metrics. Seven of
 * them at that size would be a page you scroll rather than read, and the reason
 * to group them at all was that the flat list asked the reader to hold several
 * unrelated scales at once.
 *
 * The type scale is deliberately bigger than the rest of the app: the value is
 * text-4xl and every label is text-base, never text-sm. The audience is 50+ on
 * a phone, and the number is the thing they came for.
 */
export function MetricRow({ metric, points }: { metric: TrendMetric; points: TrendPoint[] }) {
    const change = summarise(points, metric);
    const recorded = points.filter((point) => point.value !== null);

    // A <figure> because that is what this is — a chart with its caption — and
    // because it gives the whole metric one element to scope a query to.
    return (
        <figure className="m-0 py-4 first:pt-0 last:pb-0">
            <div className="flex flex-wrap items-end justify-between gap-x-4 gap-y-2">
                <figcaption>
                    <h4 className="text-base font-semibold text-gray-700">{metric.label}</h4>

                    {change.last === null ? (
                        <p className="mt-1 text-base text-gray-700">Not recorded yet</p>
                    ) : (
                        <p className="mt-1 flex flex-wrap items-baseline gap-x-2">
                            <span className="text-4xl font-bold text-gray-900 tabular-nums">
                                {metric.format(change.last)}
                            </span>
                            <span className="text-base text-gray-700">{metric.unit}</span>
                        </p>
                    )}
                </figcaption>

                {recorded.length > 0 && <Sparkline metric={metric} points={points} />}
            </div>

            {change.last !== null && (
                <p className="mt-2">
                    {change.delta === null ? (
                        <span className="text-base text-gray-700">
                            {points.length <= 1
                                ? 'Your starting point'
                                : 'Only recorded once, so there is nothing to compare yet'}
                        </span>
                    ) : (
                        <DeltaChip
                            verdict={change.verdict}
                            delta={change.delta}
                            label={changeLabel(metric, change, 'your first report')}
                        />
                    )}
                </p>
            )}

            {change.missingCount > 0 && (
                <p className="mt-1 text-sm text-gray-700">
                    {change.missingCount} of {points.length} reports did not record this.
                </p>
            )}

            {/*
                The same data as the picture, for everyone.

                A sparkline carries no labels at all, so without this the middle
                values are readable to nobody. A <details> rather than an
                sr-only table on purpose: one copy of the numbers, reachable by
                a screen reader AND by a client who just wants to read what they
                weighed in week four.
            */}
            <details className="mt-2">
                <summary className="cursor-pointer text-base font-medium text-brand-text-green underline-offset-2 hover:underline">
                    Show all {points.length} {points.length === 1 ? 'value' : 'values'}
                </summary>
                <table className="mt-2 w-full text-left text-base">
                    <caption className="sr-only">
                        {metric.label} on each of your progress reports, oldest first.
                    </caption>
                    <thead>
                        <tr className="border-b border-gray-200">
                            <th scope="col" className="py-1 font-medium text-gray-700">
                                Report
                            </th>
                            <th scope="col" className="py-1 font-medium text-gray-700">
                                Date
                            </th>
                            <th scope="col" className="py-1 text-right font-medium text-gray-700">
                                {metric.label} ({metric.unit})
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        {points.map((point) => (
                            <tr
                                key={point.index}
                                className="border-b border-gray-200 last:border-0"
                            >
                                <th scope="row" className="py-1 font-normal text-gray-700">
                                    Report {point.index + 1}
                                </th>
                                <td className="py-1 text-gray-700">
                                    {formatShortDate(point.reportDate)}
                                </td>
                                <td className="py-1 text-right text-gray-900 tabular-nums">
                                    {point.value === null
                                        ? 'Not recorded'
                                        : metric.format(point.value)}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </details>
        </figure>
    );
}

/**
 * The shape of the trend and nothing else.
 *
 * No axis, no labels, no endpoint values: the current value is already set in
 * text-4xl beside it and the table below has every reading, so a second copy of
 * "152.0" here would be ink without information.
 *
 * `aria-hidden` for the same reason it is on the full chart. An `aria-label`
 * summarising a chart gives a screen reader one sentence where a sighted reader
 * gets a shape, which is not the same information — the table is.
 *
 * The geometry comes from the same `linePaths` the full chart uses, at SPARK
 * rather than CHART. Not a second implementation: that function is the only
 * thing standing between a missing weigh-in and #278, where a null reached the
 * arithmetic and the line either plunged to y=3228 or truncated the path.
 */
function Sparkline({ metric, points }: { metric: TrendMetric; points: TrendPoint[] }) {
    const domain = domainFor(points, metric);
    const paths = linePaths(points, domain, SPARK);
    const recorded = points.filter((point) => point.value !== null);
    const last = recorded[recorded.length - 1];

    return (
        <svg
            viewBox={`0 0 ${SPARK.width} ${SPARK.height}`}
            className="h-10 w-30 shrink-0"
            aria-hidden="true"
            focusable="false"
        >
            {/* A report with no value gets a marked gap, so absent reads as
                absent rather than as a line that is straight there. */}
            {points
                .filter((point) => point.value === null)
                .map((point) => (
                    <line
                        key={`gap-${point.index}`}
                        x1={scaleX(point.index, points.length, SPARK)}
                        x2={scaleX(point.index, points.length, SPARK)}
                        y1={0}
                        y2={SPARK.height}
                        stroke="#6a7282"
                        strokeWidth={1}
                        strokeDasharray="2 2"
                    />
                ))}

            {/* One path per contiguous run. Nothing spans a gap. */}
            {paths.map((d, i) => (
                <path
                    key={i}
                    d={d}
                    fill="none"
                    stroke="#101828"
                    strokeWidth={2}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                />
            ))}

            {/* Only the latest point is marked. A lone reading has no line, so
                without this it would render as an empty box. */}
            {last && (
                <circle
                    cx={scaleX(last.index, points.length, SPARK)}
                    cy={scaleY(last.value as number, domain, SPARK)}
                    r={2.5}
                    fill="#101828"
                />
            )}
        </svg>
    );
}
